import type { Icons } from '@eppendorf/vnls-css/dist';
import type {
  DeviceConnectionStatus,
  DeviceDTO,
  UnitStateV2,
} from '@eppendorf/vnls-device-service-utils';
import {
  Button,
  Dialog,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ActivityStatus } from '$components/device/activity-status/activity-status';
import { DeviceNameCell } from '$components/device/name-cell/name-cell';
import { SectionFeedback } from '$features/dashboard/components/section-feedback';
import { ConnectionStatusBadge } from '$features/devices/connection-status-badge';

import { useDashboardDeviceStatusSummary, useDashboardLabHealth } from '../dashboard.api';
import type { DashboardMonitoringDevice } from '../dashboard.types';

import { DashboardCardTitle } from './dashboard-card-title';
import { DashboardWidget } from './dashboard-widget';

import './lab-health-comparison-card.scss';

export function LabHealthComparisonCard() {
  const { t } = useTranslation();
  const labHealth = useDashboardLabHealth();
  const deviceStatus = useDashboardDeviceStatusSummary();
  const [selectedStatus, setSelectedStatus] = useState<{
    dialogTitle: string;
    devices: DashboardMonitoringDevice[];
    deviceBadge: 'activity' | 'connection' | 'unavailable';
    sidecarRoute: 'device/details' | 'monitoring/events';
  } | null>(null);

  if (labHealth.isLoading || deviceStatus.isLoading) {
    return (
      <DashboardWidget className="lab-health-comparison-card">
        <DotsLoader />
      </DashboardWidget>
    );
  }

  if (
    !labHealth.data ||
    !deviceStatus.data ||
    labHealth.isError ||
    deviceStatus.isError
  ) {
    return (
      <DashboardWidget className="lab-health-comparison-card">
        <div className="dashboard-widget__header">
          <DashboardCardTitle
            icon="home"
            title={t('dashboard.labHealth.title')}
            hint={t('dashboard.labHealth.hint')}
          />
        </div>
        <div className="dashboard-widget__feedback" role="alert">
          <span>{t('dashboard.labHealth.loadError')}</span>
          <Button
            variant="secondary"
            onClick={() => {
              labHealth.refetch();
              deviceStatus.refetch();
            }}
          >
            {t('dashboard.labHealth.retry')}
          </Button>
        </div>
      </DashboardWidget>
    );
  }

  const { monitoringHealth } = labHealth.data;

  const monitoredStatuses = [
    {
      key: 'alerts',
      icon: 'alert' as Icons,
      label: t('dashboard.labHealth.alerts'),
      dialogTitle: t('dashboard.labHealth.alertDevicesTitle'),
      value: monitoringHealth.alertDevices,
      detail: t('dashboard.labHealth.alertsDetail'),
      devices: monitoringHealth.alertDeviceDetails,
    },
    {
      key: 'warnings',
      icon: 'warning' as Icons,
      label: t('dashboard.labHealth.monitoringWarnings'),
      dialogTitle: t('dashboard.labHealth.warningDevicesTitle'),
      value: monitoringHealth.warningDevices,
      detail: t('dashboard.labHealth.warningsDetail'),
      devices: monitoringHealth.warningDeviceDetails,
    },
    {
      key: 'normal',
      icon: 'success' as Icons,
      label: t('dashboard.labHealth.noIssues'),
      dialogTitle: t('dashboard.labHealth.noIssueDevicesTitle'),
      value: monitoringHealth.normalDevices,
      detail: t('dashboard.labHealth.normalDetail'),
      devices: monitoringHealth.normalDeviceDetails,
    },
  ];
  const notMonitoredStatuses = [
    {
      key: 'pending',
      icon: 'time' as Icons,
      label: t('dashboard.labHealth.pendingSetup'),
      dialogTitle: t('dashboard.labHealth.pendingDevicesTitle'),
      value: deviceStatus.data.pendingDevices,
      detail: t('dashboard.labHealth.pendingDetail'),
      devices: deviceStatus.data.pendingDeviceDetails,
    },
    {
      key: 'unavailable',
      icon: 'cloud-no-connection' as Icons,
      label: t('dashboard.labHealth.unavailable'),
      dialogTitle: t('dashboard.labHealth.unavailableDevicesTitle'),
      value: deviceStatus.data.unavailableDevices,
      detail: t('dashboard.labHealth.unavailableDetail'),
      devices: deviceStatus.data.unavailableDeviceDetails,
    },
  ];

  return (
    <DashboardWidget className="lab-health-comparison-card">
      <div className="dashboard-widget__header">
        <DashboardCardTitle
          icon="home"
          title={t('dashboard.labHealth.title')}
          hint={t('dashboard.labHealth.hint')}
          subheadline={t('dashboard.labHealth.deviceCount', {
            count: deviceStatus.data.totalDevices,
          })}
        />
        <SectionFeedback sectionId="lab-health-comparison" />
      </div>
      <div className="lab-health-comparison-card__groups">
        <section className="lab-health-comparison-card__group">
          <div className="lab-health-comparison-card__status-grid lab-health-comparison-card__status-grid--monitored">
            {monitoredStatuses.map((status) => (
              <button
                key={status.key}
                type="button"
                className={`lab-health-comparison-card__status lab-health-comparison-card__status--${status.key}`}
                onClick={() =>
                  setSelectedStatus({
                    dialogTitle: status.dialogTitle,
                    devices: status.devices,
                    deviceBadge: 'activity',
                    sidecarRoute: 'monitoring/events',
                  })
                }
              >
                <span className="lab-health-comparison-card__status-label">
                  <Icon name={status.icon} size={IconSizeClasses.XSmall} />
                  {status.label}
                </span>
                <strong>{status.value}</strong>
                <span className="lab-health-comparison-card__status-detail">
                  {status.detail}
                  <Icon name="right" size={IconSizeClasses.XSmall} />
                </span>
              </button>
            ))}
          </div>
        </section>
        <section className="lab-health-comparison-card__group">
          <div className="lab-health-comparison-card__status-grid lab-health-comparison-card__status-grid--not-monitored">
            {notMonitoredStatuses.map((status) => (
              <button
                key={status.key}
                type="button"
                className={`lab-health-comparison-card__status lab-health-comparison-card__status--${status.key}`}
                onClick={() =>
                  setSelectedStatus({
                    dialogTitle: status.dialogTitle,
                    devices: status.devices,
                    deviceBadge:
                      status.key === 'unavailable' ? 'unavailable' : 'connection',
                    sidecarRoute: 'device/details',
                  })
                }
              >
                <span className="lab-health-comparison-card__status-label">
                  <Icon name={status.icon} size={IconSizeClasses.XSmall} />
                  {status.label}
                </span>
                <strong>{status.value}</strong>
                <span className="lab-health-comparison-card__status-detail">
                  {status.detail}
                  <Icon name="right" size={IconSizeClasses.XSmall} />
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
      <Dialog
        isOpen={Boolean(selectedStatus)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedStatus(null);
        }}
        className="w-max-4xl lab-health-comparison-card__dialog"
      >
        <Dialog.Title>{selectedStatus?.dialogTitle}</Dialog.Title>
        <Dialog.Description className="lab-health-comparison-card__dialog-description">
          {t('dashboard.labHealth.dialogDescription')}
        </Dialog.Description>
        <ul className="lab-health-comparison-card__device-list">
          {selectedStatus?.devices.map((device) => (
            <li key={`${device.manufacturer}-${device.serialNumber}`}>
              {device.manufacturer && device.serialNumber ? (
                <Link
                  to={{
                    search: `?sidecarRoute=${selectedStatus.sidecarRoute}&manufacturer=${device.manufacturer}&serialNumber=${device.serialNumber}`,
                  }}
                  onClick={() => setSelectedStatus(null)}
                >
                  <DeviceNameCell device={device as DeviceDTO} />
                </Link>
              ) : (
                <DeviceNameCell device={device as DeviceDTO} />
              )}
              {selectedStatus.deviceBadge === 'activity' && (
                <ActivityStatus
                  activityStatus={(device.activityState as UnitStateV2) || null}
                  size="small"
                />
              )}
              {selectedStatus.deviceBadge === 'connection' && (
                <ConnectionStatusBadge
                  status={device.connectionStatus as DeviceConnectionStatus | undefined}
                  size="small"
                />
              )}
              {selectedStatus.deviceBadge === 'unavailable' && (
                <ConnectionStatusBadge size="small" />
              )}
            </li>
          ))}
        </ul>
      </Dialog>
    </DashboardWidget>
  );
}
